/**
 * Development Roadmap. ARCHITECTURE.md section 11 lists it as a navigation item, so it exists
 * in the product rather than only in a file.
 *
 * The phase list is static here and mirrors ROADMAP.md. It is deliberately not read from the
 * database: the roadmap is a document the owner approves, not application state, and putting it
 * in a table would invite it to drift from the file that governs the work.
 */

import type { ReactNode } from 'react';

interface Phase {
  number: number;
  name: string;
  status: 'COMPLETE' | 'IN PROGRESS' | 'NOT STARTED' | 'DEFERRED';
  complexity: 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY HIGH';
  objective: string;
}

/**
 * In EXECUTION ORDER, which is not numeric order. Phase numbers are stable identifiers — they are
 * referenced from code, flags, plans and three phases of history — so the 2026-09-17 revision
 * changed the order and not the numbers. ROADMAP.md §1 is the authority.
 */
const PHASES: Phase[] = [
  {
    number: 0,
    name: 'Repository & Permanent Documentation',
    status: 'COMPLETE',
    complexity: 'LOW',
    objective: 'The repo skeleton and the ten permanent documents.',
  },
  {
    number: 1,
    name: 'Foundation',
    status: 'COMPLETE',
    complexity: 'HIGH',
    objective: 'Database, events, queue, auth, environments, CI. No agents yet.',
  },
  {
    number: 2,
    name: 'Agent Platform, Control Center, Approvals, Kill Switch, Evals, CEO Briefing',
    status: 'COMPLETE',
    complexity: 'HIGH',
    objective: 'Make agents a real, observable, controllable thing before any domain agent exists.',
  },
  {
    number: 3,
    name: 'Finance I — Bookkeeping, Classification & QuickBooks sync-back',
    status: 'IN PROGRESS',
    complexity: 'VERY HIGH',
    objective:
      'Immutable transaction ingestion, QBO mirror, AI classification with confidence, and approved classifications written back to QuickBooks.',
  },
  {
    number: 4,
    name: 'Reservations & Revenue Intelligence',
    status: 'COMPLETE',
    complexity: 'HIGH',
    objective: 'Mirror Guesty; occupancy, ADR, RevPAR, pacing; the Revenue Agent.',
  },
  {
    number: 9,
    name: 'Live Data & Production Cutover (read-only)',
    status: 'NOT STARTED',
    complexity: 'HIGH',
    objective:
      'Real QuickBooks, Guesty and Stripe read connectors in production behind flags; restore drill; monitoring; sample data off.',
  },
  {
    number: 5,
    name: 'Finance II — Statements, Cash & Forecast',
    status: 'NOT STARTED',
    complexity: 'VERY HIGH',
    objective:
      'P&L, balance sheet, cash flow tied to QuickBooks to the cent; forecast, budgets, debt, CapEx. QuickBooks closes the books.',
  },
  {
    number: 6,
    name: 'Knowledge Layer & Natural-Language Command Center',
    status: 'NOT STARTED',
    complexity: 'HIGH',
    objective: 'Drive and Gmail ingestion, policies, episodes, hybrid retrieval, cited answers.',
  },
  {
    number: 7,
    name: 'Operations & Maintenance',
    status: 'NOT STARTED',
    complexity: 'MEDIUM',
    objective: 'Tickets, vendors, severity, recurrence, preventive schedules, turnover readiness.',
  },
  {
    number: 8,
    name: 'Guest Experience',
    status: 'NOT STARTED',
    complexity: 'HIGH',
    objective: 'Inbox mirror, sentiment and urgency, drafted replies. AI drafts, human approves.',
  },
  {
    number: 12,
    name: 'Controlled Autonomy',
    status: 'NOT STARTED',
    complexity: 'HIGH',
    objective: 'Promote specific agent x action pairs to Level 3 on the strength of eval history.',
  },
  {
    number: 13,
    name: 'Predictive Intelligence',
    status: 'NOT STARTED',
    complexity: 'VERY HIGH',
    objective: 'Failure prediction, forecasting improvements, realized CapEx ROI, risk scoring.',
  },
  {
    number: 10,
    name: 'Acquisitions',
    status: 'DEFERRED',
    complexity: 'MEDIUM',
    objective: 'Deferred to a later version. Interfaces only: the agent flag and its event exist.',
  },
  {
    number: 11,
    name: 'Marketing',
    status: 'DEFERRED',
    complexity: 'MEDIUM',
    objective: 'Deferred to a later version. Interfaces only: the agent flag exists.',
  },
];

export function RoadmapPage(): ReactNode {
  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Development Roadmap</h1>
          <p className="muted">
            One phase at a time, in the order shown. Each is completed, tested and reviewed before
            the next begins. This mirrors ROADMAP.md, which is the contract.
          </p>
        </div>
      </div>

      <ol className="phases">
        {PHASES.map((phase) => (
          <li
            key={phase.number}
            className={`phase phase-${phase.status.toLowerCase().replace(' ', '-')}`}
          >
            <div className="phase-head">
              <span className="phase-number">Phase {phase.number}</span>
              <span className={`status status-${phase.status.toLowerCase().replace(' ', '-')}`}>
                {phase.status}
              </span>
              <span className="muted small">{phase.complexity}</span>
            </div>
            <h2>{phase.name}</h2>
            <p className="muted">{phase.objective}</p>
          </li>
        ))}
      </ol>

      <p className="muted small">
        Complexity is estimated; development time is not, unless asked for.
      </p>
    </section>
  );
}
